import { useRef, type Ref } from 'react';
import { Button } from '@vd/ui';
import { Archive, ArrowRightLeft, Send, Tag, Unlink, UserCog, X } from 'lucide-react';
import styles from './BulkActionBar.module.css';

export interface BulkActionBarProps {
  /** The bar itself, for measuring what of the toolbar it lies over. */
  ref?: Ref<HTMLDivElement>;
  count: number;
  onMove: () => void;
  onArchive: () => void;
  onClear: () => void;
  /**
   * Called with the bounding rect of the Assign button so the parent
   * can anchor a Popover (the picker UI) precisely beneath it. Optional
   * — older callers that don't render a picker simply omit this and
   * the Assign button is hidden.
   */
  onAssign?: (anchor: DOMRect) => void;
  /** As `onAssign`, for the ticket-type picker. Omitted, the Type button is hidden. */
  onType?: (anchor: DOMRect) => void;
  /** Sends the selection to Jira (TOADO-708). Omitted, where the project cannot send or the person may not, the button is hidden. */
  onSendToJira?: () => void;
  /** Forgets the selected cards' Jira issues (TOADO-849). Omitted where none has one, or the person may not, the button is hidden. */
  onUnlinkFromJira?: () => void;
  archiving?: boolean;
  /** A bulk move is running: nothing else may start until it has settled (TOADO-976). */
  moving?: boolean;
  assigning?: boolean;
  typing?: boolean;
  sendingToJira?: boolean;
  unlinkingFromJira?: boolean;
}

/**
 * Inline bar that appears next to the view toggle / "New ticket" button
 * whenever the user has one or more tickets checked via the per-card
 * bulk-select checkbox. Parent owns the selection Set; this component is
 * pure display + callbacks.
 *
 * Hidden when `count === 0` — letting the parent keep this mounted
 * unconditionally so the bar can animate in without Layout shift.
 */
export function BulkActionBar({ ref, count, onMove, onArchive, onClear, onAssign, onType, onSendToJira, onUnlinkFromJira, archiving, moving, assigning, typing, sendingToJira, unlinkingFromJira }: BulkActionBarProps) {
  const assignBtnRef = useRef<HTMLButtonElement>(null);
  const typeBtnRef = useRef<HTMLButtonElement>(null);
  if (count === 0) return null;
  const busy = !!archiving || !!moving || !!assigning || !!typing || !!sendingToJira || !!unlinkingFromJira;
  return (
    // Focusable from code only: the toolbar hands the focus here when a
    // control that had it goes under the bar while every button is disabled.
    // Named, so landing here is announced (review round 4). A group rather
    // than a toolbar: a toolbar promises arrow keys between its buttons.
    <div
      ref={ref}
      className={styles.bar}
      data-testid="bulk-action-bar"
      tabIndex={-1}
      role="group"
      aria-label={`Bulk actions, ${count} selected`}
    >
      <span className={styles.count}>{count} selected</span>
      <div className={styles.actions}>
        {onAssign && (
          <Button
            ref={assignBtnRef}
            variant="secondary"
            size="sm"
            leftIcon={<UserCog size={13} />}
            onClick={() => {
              const r = assignBtnRef.current?.getBoundingClientRect();
              if (r) onAssign(r);
            }}
            disabled={busy}
            loading={assigning}
          >
            Assign…
          </Button>
        )}
        {onType && (
          <Button
            ref={typeBtnRef}
            variant="secondary"
            size="sm"
            leftIcon={<Tag size={13} />}
            onClick={() => {
              const r = typeBtnRef.current?.getBoundingClientRect();
              if (r) onType(r);
            }}
            disabled={busy}
            loading={typing}
          >
            Type…
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<ArrowRightLeft size={13} />}
          onClick={onMove}
          disabled={busy}
        >
          Move…
        </Button>
        {onSendToJira && (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Send size={13} />}
            onClick={onSendToJira}
            disabled={busy}
            loading={sendingToJira}
          >
            Send to Jira
          </Button>
        )}
        {onUnlinkFromJira && (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Unlink size={13} />}
            onClick={onUnlinkFromJira}
            disabled={busy}
            loading={unlinkingFromJira}
          >
            Unlink from Jira
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Archive size={13} />}
          onClick={onArchive}
          disabled={busy}
          loading={archiving}
        >
          Archive
        </Button>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<X size={13} />}
          onClick={onClear}
          disabled={busy}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}
